import { useEffect } from "react";
import { StepLoading } from "@/base/wizard/StepLoading";
import { TextField } from "@/components/TextField";
import { SelectField } from "@/components/SelectField";
import { NumericField } from "@/components/NumericField";
import { FIELD_ERROR } from "@/components/fieldStyles";
import { AddFromRecord } from "@/components/submissions/wc/AddFromRecord";
import {
  ContactFor,
  ContactsSection,
} from "@/components/submissions/wc/ContactsSection";
import { StepLoadProblem } from "@/components/submissions/wc/StepLoadProblem";
import { TWO_COLUMNS } from "@/components/submissions/wc/grids";
import {
  RepeatableRows,
  SectionCard,
  type WizardFieldErrors,
  type WizardStepContext,
} from "@/base";
import type { FieldOption } from "@/lib/brokerProducts";
import { titlesFor } from "@/lib/brokerOwnerTitles";
import type { WcDraft } from "@/lib/submissions/wc/draft";
import {
  INCLUSION_OPTIONS,
  newPerson,
  peopleCandidates,
  personFromAccount,
  personFromSubmission,
  type WcPerson,
} from "@/lib/submissions/wc/people";
import {
  locationOptions,
  OWNERS,
  peopleStepToWire,
} from "@/lib/submissions/wc/peopleStep";
import { useSubmissionLocations } from "@/lib/submissions/wc/useSubmissionLocations";
import { useSubmissionPeople } from "@/lib/submissions/wc/useSubmissionPeople";
import { newRowKey } from "@/lib/submissions/wc/rowKey";
import { useOwnerTitles } from "@/lib/useOwnerTitles";

// The Workers' Comp wizard's People step (BRK-S303, BRK-S391): the business's
// owners and officers, whether the policy covers them, and who the carrier
// can call. They are the submission's own people (PMS-S740), read when the
// step opens (`useSubmissionPeople`). An existing insured's people are copied
// in, never edited (Nav, 2026-10-08), and a contact already on the submission
// is made an owner rather than entered twice (BRK-S337). The account gets them
// at bind. Tyler's doc, Step 3, and Nav (10/21–22, BRK-S391):
// - at least one owner or officer;
// - a title from the business's legal entity's list (PMS-S813), or typed
//   when it has none;
// - the location each works at, one with payroll, chosen for the broker when
//   there is only one;
// - "Also the contact for" ticked on the owner's own row;
// - the other contacts below, in their own card.
// Owners and contacts save as one list (`peopleStepToWire`).

export function PeopleStep({
  draft,
  setDraft,
  setLoaded,
  errors,
  busy,
}: WizardStepContext<WcDraft>) {
  const people = useSubmissionPeople(draft, setLoaded);
  const places = useSubmissionLocations(draft, setLoaded);
  const titles = useOwnerTitles();
  const options =
    draft.savedLocations === null
      ? []
      : locationOptions(draft.savedLocations, draft.submission?.data ?? {});
  const only = options.length === 1 ? options[0].value : "";
  // The only location is the broker's choice made for them: a read, so it
  // never reads as an unsaved change.
  const unplaced = draft.people.some((r) => !r.locationId);
  useEffect(() => {
    if (!only || !unplaced) return;
    setLoaded((d) => ({
      ...d,
      people: d.people.map((r) =>
        r.locationId ? r : { ...r, locationId: only },
      ),
    }));
  }, [only, unplaced, setLoaded]);

  if (draft.personnelUnavailable) {
    return (
      <p className="text-body text-brk-dim">
        Owners and officers can’t be added yet: this PMS doesn’t keep a
        submission’s people.
      </p>
    );
  }
  const problem = people.problem ?? places.problem ?? titles.problem;
  if (problem) {
    const retry = people.problem
      ? people.retry
      : places.problem
        ? places.retry
        : titles.retry;
    return <StepLoadProblem problem={problem} onRetry={retry} />;
  }
  if (
    draft.personnel === null ||
    draft.savedLocations === null ||
    titles.items === null
  ) {
    return <StepLoading label="Loading the people…" />;
  }

  const rows = draft.people;
  const setRows = (next: WcPerson[]) =>
    setDraft((d) => ({ ...d, people: next }));
  const { index } = peopleStepToWire(rows, draft.contacts, draft.personnel);
  const { onSubmission, onAccount } = peopleCandidates(
    draft.account?.personnel ?? [],
    draft.personnel,
    rows,
  );
  const candidates = [
    ...onSubmission.map((p) => ({
      key: p.id,
      label: `${p.firstName} ${p.lastName} (on this submission)`,
      add: () => ({ ...personFromSubmission(p, p.id), locationId: only }),
    })),
    ...onAccount.map((p) => ({
      key: p.id,
      label: `${p.firstName} ${p.lastName}${p.title ? `, ${p.title}` : ""}`,
      add: () => personFromAccount(p, newRowKey(), only),
    })),
  ];
  const offered = titlesFor(titles.items, draft.business.entityType);

  return (
    <div className="flex flex-col gap-4">
      <SectionCard title="Owners and officers" titleAs="h3">
        <div className="flex flex-col gap-4">
          <AddFromRecord
            label="Add someone already on file"
            placeholder="Choose a person"
            choices={candidates}
            onAdd={(key) => {
              const c = candidates.find((choice) => choice.key === key);
              if (!c) return;
              // A contact made an owner leaves the other contacts: their
              // roles are ticked on their owner row now.
              setDraft((d) => ({
                ...d,
                people: [...d.people, c.add()],
                contacts: d.contacts.filter((r) => r.id !== key),
              }));
            }}
            disabled={busy}
          />
          <RepeatableRows
            label="Owners and officers"
            headingLevel={4}
            rows={rows}
            onChange={setRows}
            newRow={() => newPerson(newRowKey(), only)}
            rowKey={(row) => row.key}
            itemLabel={(_row, i) => `Person ${i + 1}`}
            addLabel="Add a person"
            renderRow={(row, update) => (
              <PersonFields
                row={row}
                update={update}
                titles={offered}
                locations={options}
                errors={errors}
                at={(field) => `personnel.${index[row.key]}.${field}`}
                busy={busy}
              />
            )}
          />
          {errors[OWNERS] && (
            <p role="alert" className={FIELD_ERROR}>
              {errors[OWNERS]}
            </p>
          )}
          {errors.personnel && (
            <p className="text-sm text-brk-danger">{errors.personnel}</p>
          )}
        </div>
      </SectionCard>
      <ContactsSection
        draft={draft}
        setDraft={setDraft}
        errors={errors}
        busy={busy}
        index={index}
      />
    </div>
  );
}

function PersonFields({
  row,
  update,
  titles,
  locations,
  errors,
  at,
  busy,
}: {
  row: WcPerson;
  update: (next: WcPerson) => void;
  /** The legal entity's titles; none means the title is typed. */
  titles: readonly string[];
  /** The submission's locations with payroll. */
  locations: readonly FieldOption[];
  errors: WizardFieldErrors;
  /** Where this row's fields are keyed, as PMS keys its refusals. */
  at: (field: string) => string;
  busy: boolean;
}) {
  const set = (patch: Partial<WcPerson>) => update({ ...row, ...patch });
  // A title PMS holds that the list doesn't offer stays one to keep.
  const titleOptions = [
    ...titles,
    ...(row.title && !titles.includes(row.title) ? [row.title] : []),
  ].map((t) => ({ value: t, label: t }));
  return (
    <div className={TWO_COLUMNS}>
      <TextField
        label="First name"
        value={row.firstName}
        onChange={(e) => set({ firstName: e.target.value })}
        error={errors[at("firstName")]}
        disabled={busy}
        required
      />
      <TextField
        label="Last name"
        value={row.lastName}
        onChange={(e) => set({ lastName: e.target.value })}
        error={errors[at("lastName")]}
        disabled={busy}
        required
      />
      {titles.length > 0 ? (
        <SelectField
          label="Title"
          options={titleOptions}
          placeholder="Choose a title"
          value={row.title}
          onChange={(e) => set({ title: e.target.value })}
          error={errors[at("title")]}
          disabled={busy}
          required
        />
      ) : (
        <TextField
          label="Title"
          value={row.title}
          onChange={(e) => set({ title: e.target.value })}
          error={errors[at("title")]}
          disabled={busy}
          required
        />
      )}
      <SelectField
        label="Location"
        options={[...locations]}
        placeholder="Choose a location"
        value={row.locationId}
        onChange={(e) => set({ locationId: e.target.value })}
        error={errors[at("locationId")]}
        disabled={busy}
        required
      />
      <NumericField
        kind="percent"
        label="Ownership"
        value={row.ownershipPercentage}
        onChange={(ownershipPercentage) => set({ ownershipPercentage })}
        error={errors[at("ownershipPercentage")]}
        optional
      />
      <SelectField
        label="Covered by the policy"
        options={INCLUSION_OPTIONS}
        placeholder="Choose included or excluded"
        value={row.wcInclusion}
        onChange={(e) =>
          set({
            wcInclusion:
              INCLUSION_OPTIONS.find((o) => o.value === e.target.value)
                ?.value ?? "",
          })
        }
        error={errors[at("wcInclusion")]}
        disabled={busy}
        required
      />
      {row.wcInclusion === "included" && (
        // Excluded keeps the payroll rather than clearing it: switching back
        // shouldn't lose what was typed.
        <div className="flex flex-col gap-1">
          <NumericField
            kind="currency"
            label="Annual payroll"
            value={row.annualRemuneration}
            onChange={(annualRemuneration) => set({ annualRemuneration })}
            error={errors[at("annualRemuneration")]}
            optional
          />
          <p className="text-xs text-brk-dim">
            Carriers may require payroll for an included owner or officer, with
            a state minimum.
          </p>
        </div>
      )}
      <div className="sm:col-span-2">
        <ContactFor
          legend="Also the contact for"
          value={row.contactRoles}
          onChange={(contactRoles) => set({ contactRoles })}
          error={errors[at("roles")]}
          disabled={busy}
        />
      </div>
    </div>
  );
}
